import { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import FirebaseDashboardListener from "../Services/FirebaseDashboardListener";
import { useFetchDashboardData } from "../../queries";
import { useTitle } from "../../config/global";
import Layout from "../Layout/Layout";
import axiosInstance from "../../config/Axios";
import { useHasFeature } from "../../hooks/useHasFeature";
import { useCustomToast } from "../Contexts/ToastContext";
import { deriveDeviceCounts } from "../../utils/deviceCounts";
import "./Dashboard.css";

// Latin digits everywhere on this page (platform rule, 2026-09): locale
// 'ar' (NOT 'ar-EG', which defaults to Eastern Arabic-Indic digits) with
// numberingSystem forced to 'latn' gives Latin digits + Latin separators.
const nfInt = new Intl.NumberFormat('ar', { numberingSystem: 'latn', maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('ar', { numberingSystem: 'latn', minimumFractionDigits: 1, maximumFractionDigits: 1 });

const Dashboard = () => {
  const { t } = useTranslation();
  useTitle("Dashboard");
  const { companyDetails } = useSelector((state) => state.global);
  const { showToast } = useCustomToast();

  const { data, error, refetch, isLoading } = useFetchDashboardData();
  const counts = deriveDeviceCounts(data);

  // Ad earnings only exist for ad_marketplace (Pro) venues; don't fire the call
  // for tiers without the feature (it would 402, or 404 until the endpoint is
  // built) — and the tile itself is hidden below when the flag is off, rather
  // than sitting there forever showing "—".
  const hasAdMarketplace = useHasFeature("ad_marketplace");
  const hasHappyHour = useHasFeature("happy_hour");
  const [adEarningsToday, setAdEarningsToday] = useState(null);
  useEffect(() => {
    if (!hasAdMarketplace) return;
    axiosInstance.get("/ad-marketplace/earnings")
      .then(res => setAdEarningsToday(res.data?.data?.today_points ?? 0))
      .catch((err) => {
        // Not swallowed: surfaced the same way every other secondary fetch
        // on this platform reports a failure (useGetScenes' onError, etc).
        showToast('error', t('dashboard.ad_earnings_today'), err.response?.data?.message);
      });
  }, [hasAdMarketplace, showToast, t]);

  const usedGB = (companyDetails?.company?.total_media_usage ?? 0) / (1024 ** 3);
  const maxGB = Math.max(
    companyDetails?.package?.features?.["media_storage_capacity"] ?? 0,
    (companyDetails?.company?.allowed_media_usage ?? 0) / (1024 ** 3)
  );
  const remainingGB = Math.max(0, maxGB - usedGB);
  const usedPct = maxGB > 0 ? Math.min(100, (usedGB / maxGB) * 100) : 0;
  // companyDetails itself hasn't necessarily resolved yet even once
  // isLoading (the /dashboard query) goes false — CompanyDetailsRouter's
  // own effect can still be pending. Gate the storage skeleton on that too.
  const storageReady = !!companyDetails?.company;

  // The welcome line must never contradict the tiles below: it is derived from the
  // same /dashboard counts, and says nothing reassuring while they are unknown.
  const welcomeText = (() => {
    if (isLoading || error || !data) return t('dashboard.welcome_neutral');
    if (counts.total === 0) return t('dashboard.welcome_no_screens');
    if (counts.offline === 0 && counts.notShowingNow === 0 && counts.onlineOnly >= counts.total) return t('dashboard.welcome_all_ok');
    return t('dashboard.welcome_attention', { offline: counts.offline, standby: counts.notShowingNow });
  })();

  return (
    // The Navbar's own sub-bar shows an English route-title ("Dashboard",
    // via useTitle above) that duplicates this page's own Arabic <h1> below
    // and reads wrong in an RTL app. That title bar is a shared Layout/
    // Navbar element (not page-scoped), and its full redesign belongs to
    // the upcoming shell migration — not this task. Blanking just the text
    // via the existing title override prop removes the duplicate without
    // touching Navbar.jsx or any other page.
    <Layout subNavbarProps={{ title: '' }}>
      <div className="pp-dash">
        <header className="pp-dash-head">
          <div>
            <h1>{t('nav.dashboard')}</h1>
            {companyDetails?.company?.name && (
              <div className="pp-dash-sub">{companyDetails.company.name}</div>
            )}
          </div>
        </header>

        {/* Welcome + storage */}
        <div className="pp-dash-toprow">
          <div className="pp-dash-welcome">
            <div className="pp-dash-glow" />
            <h2>{t('dashboard.welcome', { name: localStorage.getItem("name") })}</h2>
            <p>{welcomeText}</p>
            <Link to="/create" className="pp-dash-cta">{t('dashboard.welcome_cta')}</Link>
          </div>

          <div className="pp-dash-storage">
            {!storageReady ? (
              <>
                <div className="pp-dash-storage-top">
                  <div className="pp-dash-sk pp-dash-storage-sk--icon" />
                  <div style={{ flex: 1 }}><div className="pp-dash-sk pp-dash-storage-sk--line" style={{ width: '70%' }} /></div>
                </div>
                <div className="pp-dash-sk" style={{ height: 10, borderRadius: 999 }} />
              </>
            ) : (
              <>
                <div className="pp-dash-storage-top">
                  <div className="pp-dash-storage-icon">
                    <svg viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" /></svg>
                  </div>
                  <div>
                    <div className="pp-dash-storage-lbl">{t('dashboard.storage')}</div>
                    <div className="pp-dash-storage-val tnum">
                      {nf1.format(usedGB)} <small>/ {nfInt.format(maxGB)} {t('dashboard.storage_unit')}</small>
                    </div>
                  </div>
                </div>
                <div className="pp-dash-track"><div className="pp-dash-fill" style={{ width: `${usedPct}%` }} /></div>
                <div className="pp-dash-storage-foot">{t('dashboard.storage_remaining', { gb: nf1.format(remainingGB) })}</div>
              </>
            )}
          </div>
        </div>

        {/* Devices trio + ad earnings, OR error, OR skeleton */}
        {error ? (
          <div className="pp-panel">
            <div className="pp-dash-errbox">
              <div className="pp-dash-eic"><i className="pi pi-exclamation-triangle" /></div>
              <div className="pp-dash-err-t">{t('dashboard.error_title')}</div>
              <p className="pp-dash-err-s">{t('dashboard.error_desc')}</p>
              <button type="button" className="pp-dash-retry" onClick={() => refetch()}>{t('dashboard.retry')}</button>
            </div>
          </div>
        ) : (
          <div className="pp-stats">
            {isLoading ? (
              <>
                <div className="pp-stat" style={{ position: 'relative' }}>
                  <div className="pp-dash-sk pp-dash-sk--icon" />
                  <div style={{ paddingTop: 2 }}><div className="pp-dash-sk pp-dash-sk--a" /><div className="pp-dash-sk pp-dash-sk--b" /></div>
                </div>
                <div className="pp-stat" style={{ position: 'relative' }}>
                  <div className="pp-dash-sk pp-dash-sk--icon" />
                  <div style={{ paddingTop: 2 }}><div className="pp-dash-sk pp-dash-sk--a" /><div className="pp-dash-sk pp-dash-sk--b" /></div>
                </div>
                <div className="pp-stat" style={{ position: 'relative' }}>
                  <div className="pp-dash-sk pp-dash-sk--icon" />
                  <div style={{ paddingTop: 2 }}><div className="pp-dash-sk pp-dash-sk--a" /><div className="pp-dash-sk pp-dash-sk--b" /></div>
                </div>
                <div className="pp-stat" style={{ position: 'relative' }}>
                  <div className="pp-dash-sk pp-dash-sk--icon" />
                  <div style={{ paddingTop: 2 }}><div className="pp-dash-sk pp-dash-sk--a" /><div className="pp-dash-sk pp-dash-sk--b" /></div>
                </div>
              </>
            ) : (
              <>
                <div className="pp-stat">
                  <div className="pp-stat-head">
                    <span className="pp-stat-name">{t('dashboard.total_devices')}</span>
                    <span className="pp-stat-icon pp-stat-icon--brand">
                      <svg viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8" /></svg>
                    </span>
                  </div>
                  <div className="pp-stat-value tnum">{nfInt.format(data?.totalDevice ?? 0)}</div>
                  <div className="pp-stat-delta">{t('dashboard.total_devices_delta')}</div>
                </div>
                <div className="pp-stat">
                  <div className="pp-stat-head">
                    <span className="pp-stat-name">{t('dashboard.online_screen')}</span>
                    <span className="pp-stat-icon pp-stat-icon--brand">
                      <svg viewBox="0 0 24 24"><path d="M5 12l4 4L19 7" /></svg>
                    </span>
                  </div>
                  {/* ONLINE only. Build 380 fix (item I): this used to be
                      DERIVED client-side (totalOnlineDevice minus standby
                      minus app_closed) -- still a client-side computation
                      of device status. Now reads totalOnlineNowDevice
                      straight off the backend (DashboardController::
                      index()); deriveDeviceCounts() just passes it
                      through, computes nothing. totalOnlineDevice itself
                      stays unchanged (still includes standby/app_closed). */}
                  <div className="pp-stat-value tnum pp-stat-value--good">{nfInt.format(counts.onlineOnly)}</div>
                  <div className="pp-stat-delta">{t('dashboard.online_screen_delta')}</div>
                </div>
                <div className="pp-stat">
                  <div className="pp-stat-head">
                    <span className="pp-stat-name">{t('dashboard.not_showing_now')}</span>
                    <span className="pp-stat-icon pp-stat-icon--brand">
                      <svg viewBox="0 0 24 24"><path d="M12 7v5l3 3" /><circle cx="12" cy="12" r="9" /></svg>
                    </span>
                  </div>
                  <div className="pp-stat-value tnum">{nfInt.format(counts.notShowingNow)}</div>
                  <div className="pp-stat-delta">
                    {nfInt.format(counts.standby)} {t('dashboard.standby_count_label')}
                    {' · '}
                    <span style={counts.appClosed > 0 ? { color: 'var(--pp-btn-honey-hover)', fontWeight: 700 } : undefined}>
                      {nfInt.format(counts.appClosed)} {t('dashboard.app_closed_count_label')}
                    </span>
                  </div>
                </div>
                <div className="pp-stat">
                  <div className="pp-stat-head">
                    <span className="pp-stat-name">{t('dashboard.offline_screen')}</span>
                    <span className="pp-stat-icon pp-stat-icon--brand">
                      <svg viewBox="0 0 24 24"><path d="M18.4 18.4A10 10 0 0 0 5.6 5.6M1 1l22 22M8.5 16.5a5 5 0 0 1 7 0" /></svg>
                    </span>
                  </div>
                  <div className="pp-stat-value tnum pp-stat-value--crit">{nfInt.format(counts.offline)}</div>
                  <div className="pp-stat-delta">{t('dashboard.offline_screen_delta')}</div>
                </div>
                {hasAdMarketplace && (
                  <Link to="/billing/ad-earnings" className="pp-stat" aria-label={t('dashboard.ad_earnings_today')}>
                    <div className="pp-stat-head">
                      <span className="pp-stat-name">{t('dashboard.ad_earnings_today')}</span>
                      <span className="pp-stat-icon pp-stat-icon--brand">
                        <svg viewBox="0 0 24 24"><path d="M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                      </span>
                    </div>
                    <div className="pp-stat-value tnum">
                      {adEarningsToday !== null ? nfInt.format(adEarningsToday) : '—'}
                    </div>
                    <div className="pp-stat-delta" style={{ color: 'var(--pp-btn-honey-hover)', fontWeight: 700 }}>
                      {t('dashboard.ad_earnings_delta')}
                    </div>
                  </Link>
                )}
              </>
            )}
          </div>
        )}

        {/* Quick actions */}
        <div className="pp-dash-sechead">{t('dashboard.quick_actions_title')}</div>
        <div className="pp-stats pp-stats--actions">
          <Link to="/create" className="pp-stat pp-stat--action">
            <div className="pp-stat-action-row">
              <span className="pp-stat-icon pp-stat-icon--brand">
                <svg viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 9v5M9.5 11.5h5" /></svg>
              </span>
              <span className="pp-stat-action-text">
                <span className="pp-stat-action-title">{t('dashboard.action_create_show_title')}</span>
                <span className="pp-stat-action-sub">{t('dashboard.action_create_show_desc')}</span>
              </span>
              <span className="pp-stat-action-arrow">←</span>
            </div>
          </Link>
          <Link to="/devices/list" className="pp-stat pp-stat--action">
            <div className="pp-stat-action-row">
              <span className="pp-stat-icon pp-stat-icon--brand">
                <svg viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M18.5 3.5v5M21 6h-5" /></svg>
              </span>
              <span className="pp-stat-action-text">
                <span className="pp-stat-action-title">{t('dashboard.action_new_screen_title')}</span>
                <span className="pp-stat-action-sub">{t('dashboard.action_new_screen_desc')}</span>
              </span>
              <span className="pp-stat-action-arrow">←</span>
            </div>
          </Link>
          {hasHappyHour && (
            <Link to="/apps/happy-hour" className="pp-stat pp-stat--action">
              <div className="pp-stat-action-row">
                <span className="pp-stat-icon pp-stat-icon--brand">
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5M9 2h6" /></svg>
                </span>
                <span className="pp-stat-action-text">
                  <span className="pp-stat-action-title">{t('dashboard.action_happy_hour_title')}</span>
                  <span className="pp-stat-action-sub">{t('dashboard.action_happy_hour_desc')}</span>
                </span>
                <span className="pp-stat-action-arrow">←</span>
              </div>
            </Link>
          )}
        </div>
      </div>
      <FirebaseDashboardListener path={"/Dashboard/" + localStorage.getItem("company_id") + "/action"} getData={refetch} />
    </Layout>
  );
};
export default Dashboard;
